import React, { memo, useEffect, useRef } from '../../lib/teact/teact';
import { getActions, withGlobal } from '../../global';

import type { GlobalState } from '../../global/types';
import { SignDataState } from '../../global/types';

import { getDoesUsePinPad } from '../../util/biometrics';
import buildClassName from '../../util/buildClassName';
import { pick } from '../../util/iteratees';
import { MEMO_EMPTY_ARRAY } from '../../util/memo';
import resolveSlideTransitionName from '../../util/resolveSlideTransitionName';

import useLang from '../../hooks/useLang';
import useLastCallback from '../../hooks/useLastCallback';
import useModalTransitionKeys from '../../hooks/useModalTransitionKeys';

import ConfirmationHeader from '../common/ConfirmationHeader';
import Modal from '../ui/Modal';
import ModalHeader from '../ui/ModalHeader';
import PasswordForm from '../ui/PasswordForm';
import Transition from '../ui/Transition';
import DappSignDataInitial from './DappSignDataInitial';

import modalStyles from '../ui/Modal.module.scss';
import styles from './Dapp.module.scss';

type StateProps = Pick<GlobalState['currentDappSignData'], 'isLoading' | 'state' | 'promiseId' | 'dapp' | 'error'> & {
  isAppLocked?: boolean;
};

function DappSignDataModal({
  isLoading,
  state,
  promiseId,
  dapp,
  error,
  isAppLocked,
}: StateProps) {
  const {
    setDappSignDataScreen,
    submitDappSignData,
    clearDappSignDataError,
    closeDappSignData,
    cancelDappSignData,
  } = getActions();

  const lang = useLang();

  const isOpen = state !== SignDataState.None;

  // The close animation can end after a newer request opened, so it cancels the request this modal showed when the
  // close began, snapshotted at that moment
  const shownPromiseIdRef = useRef<string>();
  const closingPromiseIdRef = useRef<string>();
  useEffect(() => {
    if (isOpen) {
      shownPromiseIdRef.current = promiseId;
    } else {
      closingPromiseIdRef.current = shownPromiseIdRef.current;
    }
  }, [isOpen, promiseId]);

  const { renderingKey, nextKey, updateNextKey } = useModalTransitionKeys(state, isOpen);

  const handleBackClick = useLastCallback(() => {
    if (state === SignDataState.Password) {
      setDappSignDataScreen({ state: SignDataState.Initial });
    }
  });

  const handlePasswordSubmit = useLastCallback((enclaveToken: string) => {
    submitDappSignData({ enclaveToken });
  });

  const handleResetSignData = useLastCallback(() => {
    cancelDappSignData({ promiseId: closingPromiseIdRef.current });
    updateNextKey();
  });

  function renderPassword(isActive: boolean) {
    return (
      <>
        {!getDoesUsePinPad() && (
          <ModalHeader title={lang('Confirm')} onClose={closeDappSignData} />
        )}
        <PasswordForm
          isActive={isActive}
          isLoading={isLoading}
          operationTitle="Confirm"
          noAnimatedIcon
          error={error}
          submitLabel={lang('Confirm')}
          cancelLabel={lang('Back')}
          noAutoConfirm
          onAuthorize={handlePasswordSubmit}
          onCancel={handleBackClick}
          onUpdate={clearDappSignDataError}
        >
          <ConfirmationHeader
            assets={MEMO_EMPTY_ARRAY}
            title={lang('Sign Data')}
            subtitlePrefix={lang('$dapp_sign_data_for_prefix')}
            dapp={dapp}
          />
        </PasswordForm>
      </>
    );
  }

  function renderContent(isActive: boolean, isFrom: boolean, currentKey: SignDataState) {
    switch (currentKey) {
      case SignDataState.Initial:
        return <DappSignDataInitial isActive={isActive} />;

      case SignDataState.Password:
        return renderPassword(isActive);
    }
  }

  return (
    <Modal
      hasCloseButton
      isOpen={isOpen}
      isKeyboardDisabled={isAppLocked}
      dialogClassName={styles.modalDialog}
      noBackdropClose
      onClose={closeDappSignData}
      onCloseAnimationEnd={handleResetSignData}
    >
      <Transition
        name={resolveSlideTransitionName()}
        className={buildClassName(modalStyles.transition, modalStyles.transition_stableScroll, 'custom-scroll')}
        slideClassName={modalStyles.transitionSlide}
        activeKey={renderingKey}
        nextKey={nextKey}
      >
        {renderContent}
      </Transition>
    </Modal>
  );
}

export default memo(withGlobal((global): StateProps => ({
  ...pick(global.currentDappSignData, ['isLoading', 'state', 'promiseId', 'dapp', 'error']),
  isAppLocked: global.isAppLockActive,
}))(DappSignDataModal));
